import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Component, ViewChild } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ModulesService } from '../../services/modules.service';
import { User, UserTier } from '../../models/User';
import { firstValueFrom } from 'rxjs';
import { ScoreService } from '../../services/score.service';
import { QuestionsService } from '../../services/questions.service';
import { Question, QuestionType } from '../../models/QuestionResponse';
import { Module } from '../../models/Module';
import { QuizzComponent } from '../questions/quizz/quizz.component';
import { CorrectionItem } from '../../models/CorrectionItem';
import { CorrectionComponent } from '../questions/correction/correction.component';
import { JeuflecheComponent } from '../questions/jeufleche/jeufleche.component';
import { ToastrService } from 'ngx-toastr';
import { GonogoComponent } from '../questions/gonogo/gonogo.component';
import { CogresComponent } from '../questions/cogres/cogres.component';
import { PuzzleComponent } from '../questions/puzzle/puzzle.component';
import { TrousComponent } from '../questions/trous/trous.component';
import { SimulationComponent } from '../questions/simulation/simulation.component';
import { SimulationState } from '../../models/SimulationMessage';
import { Role } from '../../models/Role';
import { PaymentModalComponent } from '../modales/payment-modal/payment-modal.component';
import { ModalService } from '../../services/modal.service';
import { TranslationService } from '../../services/translation.service';
import { LockedContentModalComponent } from '../modales/locked-content-modal/locked-content-modal.component';
import { PremiumOfferService } from '../../services/premium-offer.service';

enum QuestionViewMode {
  question,
  answer,
}

@Component({
  selector: 'app-module-take',
  standalone: true,
  imports: [CommonModule, TranslateModule, QuizzComponent, CorrectionComponent, JeuflecheComponent, GonogoComponent, CogresComponent, PuzzleComponent, TrousComponent, SimulationComponent],
  templateUrl: './module-take.component.html',
  styleUrl: './module-take.component.scss',
  animations: [
    trigger('overlayAnimation', [
      state('hidden', style({ opacity: 0 })),
      state('visible', style({ opacity: 1 })),
      transition('hidden => visible', animate('300ms ease-in')),
      transition('visible => hidden', animate('300ms ease-out')),
    ]),
  ],
})
export class ModuleTakeComponent {
  QuestionType = QuestionType;
  QuestionViewMode = QuestionViewMode;
  qIndex?: number;
  moduleId: string;
  color?: string;
  user?: User;
  graphism?: string;
  module?: Module;
  questions: Question[] = [];
  pickedQuizzAnswers: string[] = [];
  pickedJeuFlecheAnswers: string[] = [];
  pickedPuzzleAnswer: string = '';
  viewMode: QuestionViewMode = QuestionViewMode.question;
  corrections: CorrectionItem[] = [];
  onlyPicked: boolean = false;
  isTp: boolean = false;
  isLoadingAnswer: boolean = false;
  showCorrection: boolean = false;
  simulationCompleted: boolean = false;

  @ViewChild('cogres', { static: false }) cogresChild?: CogresComponent;
  @ViewChild('gonogo', { static: false }) gonogoChild?: GonogoComponent;
  @ViewChild('jeufleche', { static: false })
  jeuflecheChild?: JeuflecheComponent;
  @ViewChild('puzzle', { static: false }) puzzleChild?: PuzzleComponent;
  @ViewChild('quizz', { static: false }) quizzChild?: QuizzComponent;
  @ViewChild('trous', { static: false }) trousChild?: TrousComponent;
  @ViewChild('simulation', { static: false })
  simulationChild?: SimulationComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toaster: ToastrService,
    private authService: AuthService,
    private moduleService: ModulesService,
    private scoreService: ScoreService,
    private questionService: QuestionsService,
    private modalService: ModalService,
    private translationService: TranslationService,
    private premiumOfferService: PremiumOfferService,
  ) {}

  ngOnInit() {
    this.isTp = this.router.url.includes('_tp');
    this.route.params.subscribe((params: Params) => {
      this.moduleId = params['id'];
      this.color = params['color'];
      this.updateInfos();
    });
    this.validate = this.validate.bind(this);
  }

  private getRole(): Role {
    return this.isTp ? Role.professeur : Role.enfant;
  }

  private async updateInfos() {
    this.user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());
    this.graphism = this.user?.graphism;

    let modules = await firstValueFrom(this.moduleService.getAllModules());
    let scores = await firstValueFrom(this.authService.getMyScores(this.getRole()));
    let moduleScore = scores.data[0].progresses.find((elt) => elt.module_id === this.moduleId);
    let module = modules.find((elt) => elt.module_enum === this.moduleId);
    let questions = await firstValueFrom(this.questionService.getQuestionsForModule(this.moduleId));
    if (!moduleScore) {
      return;
    }

    let nextLevel = this.scoreService.getNextLevel(moduleScore.validated_leagues);

    if (!nextLevel) {
      return;
    }

    const shouldAllow = await this.moduleService.canSelectModule(this.user?.tier ?? UserTier.free, nextLevel, this.moduleId);

    if (shouldAllow) {
      let filteredQuestions = questions.data.filter((elt) => elt.level_id === nextLevel);
      //   this.questions = questions.data.filter((elt) => elt.question_type == QuestionType.simulation);

      // Filtre la dernière question si c'est une simulation
      // if (filteredQuestions.length > 0) {
      //   const lastQuestion = filteredQuestions[filteredQuestions.length - 1];
      //   if (lastQuestion.question_type === QuestionType.simulation) {
      //     filteredQuestions.pop();
      //   }
      // }

      this.questions = filteredQuestions;
      this.module = module;
      this.selectQuestion(0);
    } else {
      this.openPaymentModal();
    }
  }

  selectQuestion(index: number) {
    // Reset simulation completed flag when selecting a new question
    this.simulationCompleted = false;

    if (this.questions[index].question_type === QuestionType.quizz) {
      this.pickedQuizzAnswers = this.questions[index].answers.map((elt) => '0');
    } else if (this.questions[index].question_type === QuestionType.jeuFleche) {
      this.pickedJeuFlecheAnswers = this.questions[index].answers.map((elt) => '');
    } else if (this.questions[index].question_type === QuestionType.goNogo) {
      this.pickedQuizzAnswers = this.questions[index].answers.map((elt) => '');
    } else if (this.questions[index].question_type === QuestionType.resistanceCognitive) {
      this.pickedQuizzAnswers = this.questions[index].answers.map((elt) => '');
    } else if (this.questions[index].question_type === QuestionType.puzzleDePhrase) {
      this.pickedPuzzleAnswer = '';
    } else if (this.questions[index].question_type === QuestionType.texteATrous) {
      this.pickedPuzzleAnswer = '';
    } else if (this.questions[index].question_type === QuestionType.simulation) {
      // No special initialization needed for simulation
      // The SimulationComponent handles its own state
    }
    this.qIndex = index;
    this.viewMode = QuestionViewMode.question;
    this.showCorrection = false;
  }

  getDimensionsForIndicators(index: number): any {
    let totalWidth = window.innerWidth;
    let itemWidth = (totalWidth - (8 * (this.questions.length - 1) + 32)) / this.questions.length;
    let maxWidth = Math.min(itemWidth, 40);
    let obj: any = {
      width: `${maxWidth}px`,
      height: `${maxWidth}px`,
      border: index === this.qIndex ? 'solid 4px #007eff' : 'solid 4px white',
    };

    return obj;
  }

  getStyleForValidateButton(): any {
    let obj: any = {
      'background-color': this.moduleService.getModuleColorName(this.moduleId, this.color),
    };

    return obj;
  }

  validate() {
    if (!this.canCorrectQuestion()) {
      return;
    }

    this.correctQuestion();
  }

  async next() {
    // For simulation questions, send the answer directly and navigate
    if (this.qIndex !== undefined && this.questions[this.qIndex].question_type === QuestionType.simulation) {
      this.isLoadingAnswer = true;

      try {
        const question = this.questions[this.qIndex];
        const levelValidated = this.qIndex === this.questions.length - 1;

        // Submit the answer as correct since the simulation is complete
        await firstValueFrom(
          this.questionService.postQuestionAnswer(
            this.moduleId,
            question.question_id,
            question.question_unique_id,
            true, // shouldGoNext is true since simulation is complete
            levelValidated,
            [], // No answers needed for simulation questions
          ),
        );

        // Navigate to the next question or end the module
        if (this.qIndex === this.questions.length - 1) {
          // Last question - navigate to module end
          if (this.color) {
            this.router.navigate(['module_end_tp', this.color, this.moduleId]);
          } else {
            this.router.navigate(['module_end', this.moduleId]);
          }
        } else {
          // Not the last question - go to next question
          this.selectQuestion(this.qIndex + 1);
          this.isLoadingAnswer = false;
        }
      } catch (error) {
        console.error('Error completing simulation:', error);
        this.toaster.error(this.translationService.instant('questions.simulation.errorCompletingSimulation'), this.translationService.instant('common.labels.error'));
        this.isLoadingAnswer = false;
      }
    } else {
      // For other question types, use the standard flow
      this.goNextOrAskAgain();
    }
  }

  canCorrectQuestion(): boolean {
    if (this.qIndex === undefined) {
      return false;
    }

    switch (this.questions[this.qIndex].question_type) {
      case QuestionType.quizz:
        const canQuizz = this.pickedQuizzAnswers.filter((elt) => elt === '1').length >= 1;

        if (!canQuizz) {
          this.toaster.error(this.translationService.instant('moduleTake.quizzError'));
        }
        return canQuizz;
      case QuestionType.jeuFleche:
        const canJeuFleche = !this.pickedJeuFlecheAnswers.includes('');

        if (!canJeuFleche) {
          this.toaster.error(this.translationService.instant('moduleTake.jeufleche'));
        }

        return canJeuFleche;
      case QuestionType.goNogo:
        const canGoNogo = !this.pickedQuizzAnswers.includes('');

        if (!canGoNogo) {
          this.toaster.error(this.translationService.instant('moduleTake.goNogoError'));
        }

        return canGoNogo;
      case QuestionType.resistanceCognitive:
        return true;
      case QuestionType.puzzleDePhrase:
        let canPuzzle = false;

        if (this.puzzleChild === undefined) {
          return canPuzzle;
        }
        canPuzzle = this.puzzleChild.answers.filter((elt) => elt === null).length === 0;

        if (!canPuzzle) {
          this.toaster.error(this.translationService.instant('moduleTake.puzzleError'));
        }

        return canPuzzle;
      case QuestionType.texteATrous:
        const canTrou = this.pickedPuzzleAnswer !== '' && !this.pickedPuzzleAnswer.includes('............');

        if (!canTrou) {
          this.toaster.error(this.translationService.instant('moduleTake.wholeError'));
        }

        return canTrou;
      case QuestionType.simulation:
        return false;
    }
  }

  correctQuestion() {
    if (this.qIndex === undefined) {
      return;
    }

    this.viewMode = QuestionViewMode.answer;

    // Small delay to ensure animation triggers properly
    setTimeout(() => {
      this.showCorrection = true;
    }, 10);
    const question = this.questions[this.qIndex];
    if (question.question_type === QuestionType.quizz) {
      this.corrections = [];
      this.onlyPicked = true;

      for (let i = 0; i < question.good_answers.length; i++) {
        let correct = question.good_answers[i] === this.pickedQuizzAnswers[i];
        let correction = new CorrectionItem(correct, question.good_answers_details[i], this.pickedQuizzAnswers[i] === '1', question.answers[i]);
        this.corrections.push(correction);
      }
    } else if (question.question_type === QuestionType.jeuFleche) {
      this.corrections = [];

      for (let i = 0; i < question.good_answers.length; i++) {
        let correct = question.good_answers[i] === this.pickedJeuFlecheAnswers[i];
        let correction = new CorrectionItem(correct, question.good_answers_details[i], true, question.answers[i]);
        this.corrections.push(correction);
      }
    } else if (question.question_type === QuestionType.goNogo) {
      this.corrections = [];

      for (let i = 0; i < question.good_answers.length; i++) {
        let correct = question.good_answers[i] === this.pickedQuizzAnswers[i];
        let correction = new CorrectionItem(correct, question.good_answers_details[i], true, question.answers[i]);
        this.corrections.push(correction);
      }
    } else if (question.question_type === QuestionType.resistanceCognitive) {
      this.corrections = [];

      for (let i = 0; i < question.good_answers.length; i++) {
        let correct = question.good_answers[i] === this.pickedQuizzAnswers[i];
        let correction = new CorrectionItem(correct, question.good_answers_details[i], this.pickedQuizzAnswers[i] === '1', question.answers[i]);
        this.corrections.push(correction);
      }
    } else if (question.question_type === QuestionType.puzzleDePhrase) {
      this.corrections = [];

      const correctIndex = question.answers.findIndex((elt) => elt !== '');
      const correct = this.pickedPuzzleAnswer === question.answers[correctIndex];
      const correction = new CorrectionItem(correct, question.good_answers_details.find((elt) => elt !== undefined && elt !== null && elt !== '') ?? '', true, undefined);
      this.corrections.push(correction);
    } else if (question.question_type === QuestionType.texteATrous) {
      this.corrections = [];

      const realSentenceIndex = question.answers.findIndex((elt) => elt !== '' && !elt.includes('............'));
      const realSentence = question.answers[realSentenceIndex];
      const correct = this.pickedPuzzleAnswer === realSentence;
      const correction = new CorrectionItem(correct, question.good_answers_details.find((elt) => elt !== null) ?? '', true, undefined);
      this.corrections.push(correction);
    }
  }

  getAnswers(): string[] {
    if (this.qIndex === undefined) {
      return [];
    }

    switch (this.questions[this.qIndex].question_type) {
      case QuestionType.quizz:
        return this.pickedQuizzAnswers;
      case QuestionType.jeuFleche:
        return this.pickedJeuFlecheAnswers;
      case QuestionType.goNogo:
        return this.pickedQuizzAnswers;
      case QuestionType.resistanceCognitive:
        return this.pickedQuizzAnswers;
      case QuestionType.puzzleDePhrase:
        return [this.pickedPuzzleAnswer];
      case QuestionType.texteATrous:
        return [this.pickedPuzzleAnswer];
      case QuestionType.simulation:
        return [];
    }
  }

  async goNextOrAskAgain() {
    if (this.qIndex === undefined) {
      return;
    }

    // Start fade out animation
    this.showCorrection = false;

    // Wait for animation to complete before proceeding
    setTimeout(() => {
      this.isLoadingAnswer = true;
      this.proceedWithNextQuestion();
    }, 300);
  }

  private async proceedWithNextQuestion() {
    if (this.qIndex === undefined) {
      return;
    }

    let shouldGoNext = this.corrections.filter((elt) => !elt.correct).length === 0;
    this.corrections = [];
    this.onlyPicked = false;
    const levelValidated = this.qIndex === this.questions.length - 1;
    const answers: string[] = this.getAnswers();

    await firstValueFrom(
      this.questionService.postQuestionAnswer(this.moduleId, this.questions[this.qIndex].question_id, this.questions[this.qIndex].question_unique_id, shouldGoNext, levelValidated, answers),
    );

    if (shouldGoNext) {
      if (this.qIndex == this.questions.length - 1) {
        // finish questionnaire
        if (this.color) {
          this.router.navigate(['module_end_tp', this.color, this.moduleId]);
        } else {
          this.router.navigate(['module_end', this.moduleId]);
        }
      } else {
        // go next question
        this.selectQuestion(this.qIndex + 1);
        this.isLoadingAnswer = false;
      }
    } else {
      this.selectQuestion(this.qIndex!);
      this.resetQuestionIfNeeded();
      this.isLoadingAnswer = false;
    }
  }

  resetQuestionIfNeeded() {
    if (this.qIndex === undefined) {
      return;
    }

    switch (this.questions[this.qIndex].question_type) {
      case QuestionType.quizz:
        if (this.quizzChild !== undefined) {
          this.quizzChild.setupQuestion();
        }
        break;
      case QuestionType.jeuFleche:
        if (this.jeuflecheChild !== undefined) {
          this.jeuflecheChild.setupQuestion();
        }
        break;
      case QuestionType.goNogo:
        return;
      case QuestionType.resistanceCognitive:
        if (this.cogresChild !== undefined) {
          this.cogresChild.setupQuestion();
        }
        break;
      case QuestionType.puzzleDePhrase:
        if (this.puzzleChild !== undefined) {
          this.puzzleChild.setupQuestion();
        }
        break;
      case QuestionType.texteATrous:
        if (this.trousChild !== undefined) {
          this.trousChild.setupQuestion();
        }
        break;
    }
  }

  shouldDisplayValidateButton(): boolean {
    if (this.qIndex === undefined) {
      return false;
    }

    const question = this.questions[this.qIndex];

    // Hide validate button for simulation questions
    if (question.question_type === QuestionType.simulation) {
      return false;
    }

    if (question.question_type === QuestionType.quizz) {
      const goodAnswersCount = question.good_answers.filter((elt) => elt === '1').length;
      const answeredCount = this.pickedQuizzAnswers.filter((elt) => elt === '1').length;

      return this.viewMode === QuestionViewMode.question && goodAnswersCount === answeredCount;
    } else if (question.question_type === QuestionType.resistanceCognitive) {
      return false;
    }

    return this.viewMode === QuestionViewMode.question;
  }

  shouldDisplayNextButton() {
    // For simulation questions, show the next button when simulation is completed
    if (this.qIndex !== undefined && this.questions[this.qIndex].question_type === QuestionType.simulation) {
      return this.simulationCompleted && !this.isLoadingAnswer;
    }

    return this.qIndex !== undefined && this.viewMode === QuestionViewMode.answer && !this.isLoadingAnswer;
  }

  openPaymentModal() {
    if (this.premiumOfferService.canSeePremiumOffers(this.user)) {
      const modalRef = this.modalService.open(PaymentModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
      });

      modalRef.result.then((result) => {
        if (result === 'cancel') {
          this.router.navigate(['']);
        }
      });
    } else {
      const modalRef = this.modalService.open(LockedContentModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
      });

      modalRef.result.then((result) => {
        if (result === 'cancel') {
          this.router.navigate(['']);
        }
      });
    }
  }

  /**
   * Handler for simulation completion event
   * Sets a flag to show the next button - user will click it manually to proceed
   */
  async onSimulationComplete(state: SimulationState) {
    // When simulation is complete, just mark it as completed to show the next button
    if (state.isComplete && this.qIndex !== undefined) {
      this.simulationCompleted = true;
    }
  }
}
